import { createLogger } from "./logger";
import type { RequestRunner } from "./requestRunner";
import type { EventLog } from "./eventLog";

const log = createLogger("attributes");

/** Methods captured on every snapshot. Together they answer "what is this contact". */
const SNAPSHOT_METHODS = [
  "getAttributes",
  "getContact",
  "getQueue",
  "getChannelType",
  "getInitialContactId",
  "getType",
] as const;

/**
 * A point-in-time capture of a contact's attributes and identifying metadata. `values` holds
 * the parsed `getAttributes` payload; `sources` holds the raw result (or failure marker) of
 * every snapshot method, for diagnostics. `attributesError` is set when the `getAttributes`
 * request itself failed — distinct from a successful call that legitimately returned no
 * attributes, so a failed query is never misread as "the flow set nothing".
 */
export type AttributeSnapshot = {
  contactId: string;
  ts: string;
  values: Record<string, unknown>;
  sources: Record<string, unknown>;
  attributesError?: string;
};

/** The delta between two attribute snapshots. */
export type AttributeDiff = {
  added: string[];
  changed: Array<{ k: string; from: unknown; to: unknown }>;
  removed: string[];
};

const deepEqual = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * Diffs two attribute maps. Flows mutate attributes mid-contact, so the delta is the
 * diagnostic signal — raw values alone do not show what a flow actually did.
 *
 * Uses `Object.hasOwn` rather than `in` (amendment A3): `in` walks the prototype chain, so a
 * contact attribute literally named `constructor`, `toString`, or `valueOf` would never be
 * reported as added or removed, and would be compared against `Object.prototype`'s member
 * instead of the real value. Amazon Connect flows can set arbitrary attribute names.
 */
export function diffAttributes(
  prev: Record<string, unknown> | undefined,
  next: Record<string, unknown>,
): AttributeDiff {
  const before = prev ?? {};
  const added = Object.keys(next).filter((k) => !Object.hasOwn(before, k));
  const removed = Object.keys(before).filter((k) => !Object.hasOwn(next, k));
  const changed = Object.keys(next)
    .filter((k) => Object.hasOwn(before, k) && !deepEqual(before[k], next[k]))
    .map((k) => ({ k, from: before[k], to: next[k] }));
  return { added, changed, removed };
}

/** Extracted result of the `getAttributes` snapshot call, keeping success and failure distinct. */
type AttributesResult = {
  raw: unknown;
  failed: boolean;
  errorMessage?: string;
};

/** Captures and diffs contact attribute snapshots over the life of each contact. */
export class AttributeTracker {
  private readonly snapshots = new Map<string, AttributeSnapshot[]>();

  constructor(
    private readonly runner: RequestRunner,
    private readonly eventLog: EventLog,
  ) {}

  /**
   * Fires every snapshot method for a contact and stores the combined result. `RequestRunner`
   * never throws — a failed SDK call comes back as an `entry.kind === "error"` entry whose
   * `payload` is undefined, so each method's outcome is checked explicitly rather than assumed
   * to carry data.
   */
  async capture(contactId: string): Promise<AttributeSnapshot> {
    const sources: Record<string, unknown> = {};
    let attributesResult: AttributesResult = { raw: undefined, failed: false };

    for (const method of SNAPSHOT_METHODS) {
      const entry = await this.runner.invoke("contact", method, { contactId });
      if (entry.kind === "error") {
        const errorMessage = entry.error?.message ?? "request failed";
        sources[method] = { error: errorMessage };
        if (method === "getAttributes")
          attributesResult = { raw: undefined, failed: true, errorMessage };
      } else {
        sources[method] = entry.payload;
        if (method === "getAttributes")
          attributesResult = { raw: entry.payload, failed: false };
      }
    }

    const values = this.resolveValues(attributesResult);
    const snapshot: AttributeSnapshot = {
      contactId,
      ts: new Date().toISOString(),
      values,
      sources,
      ...(attributesResult.failed && {
        attributesError: attributesResult.errorMessage,
      }),
    };

    this.recordSnapshot(contactId, snapshot);
    return snapshot;
  }

  /**
   * Resolves the `values` map for a snapshot from the `getAttributes` outcome. A failed
   * request and a successful-but-empty request both surface `{}` here — the distinction lives
   * in `attributesError` on the snapshot, not in the shape of `values`.
   */
  private resolveValues(result: AttributesResult): Record<string, unknown> {
    if (result.failed) return {};
    return result.raw && typeof result.raw === "object"
      ? (result.raw as Record<string, unknown>)
      : {};
  }

  /** Appends the snapshot to history and logs a diff against the prior snapshot, if any. */
  private recordSnapshot(contactId: string, snapshot: AttributeSnapshot): void {
    const history = this.snapshots.get(contactId) ?? [];
    const previous = history[history.length - 1];
    history.push(snapshot);
    this.snapshots.set(contactId, history);

    const delta = diffAttributes(previous?.values, snapshot.values);
    if (delta.added.length || delta.changed.length || delta.removed.length) {
      this.eventLog.append({
        kind: "log",
        moduleId: "contact",
        name: "attributesChanged",
        contactId,
        payload: delta,
      });
      log.info("attributes changed", { contactId, ...delta });
    }
  }

  /** All snapshots captured for a contact, oldest first. */
  history(contactId: string): AttributeSnapshot[] {
    return this.snapshots.get(contactId) ?? [];
  }

  /** Diff between the two most recent snapshots for a contact. */
  latestDiff(contactId: string): AttributeDiff | undefined {
    const h = this.history(contactId);
    if (h.length === 0) return undefined;
    return diffAttributes(h[h.length - 2]?.values, h[h.length - 1]!.values);
  }
}
